import React, { useState, useEffect, useRef, useCallback } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"

import {
  submitPricingRequest,
  usePricingUnlocked,
} from "../services/pricingAccess"

import "../styles/pricingGate.scss"

/**
 * THE PRICING GATE.
 *
 * Nothing is emailed and no document is generated. Name, company and work email
 * reveal the figure IN PLACE — the trigger is replaced by the price it was
 * hiding — and a first-party cookie keeps it revealed on the next visit.
 *
 * This component renders the ASK only. Whether a price or a gate appears at a
 * given spot is decided at the call site with usePricingUnlocked(), because
 * only the call site knows what the revealed thing should look like: a $fs-stat
 * figure in the offer page's terms block, a short string in a catalogue row, an
 * exclusions list in the scope column.
 *
 * THE THREE DIAGNOSTIC OFFERS ARE NEVER GATED. See ENTRY_SLUGS in
 * src/data/offers.js.
 *
 * Accessibility: role="dialog" + aria-modal, labelled by its own heading, focus
 * moved in and handed back to the trigger, Tab trapped, Escape closes,
 * background scroll locked.
 */

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

/**
 * Free-mail domains, rejected because the field asks for a work address.
 *
 * A short list on purpose: it catches the common cases without becoming a
 * blocklist to maintain, and it never rejects a company domain it has not heard
 * of. If it turns away people you want, delete the check — one function, one
 * call site.
 */
const FREE_MAIL = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "hotmail.com",
  "hotmail.co.uk", "outlook.com", "live.com", "msn.com", "aol.com",
  "icloud.com", "me.com", "mail.com", "gmx.com", "yandex.com",
  "proton.me", "protonmail.com", "zoho.com",
]

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const validate = ({ name, company, email }) => {
  if (!name.trim()) return "Add your name."
  if (!company.trim()) return "Add your company."
  // Deliberately loose. Anything stricter rejects valid addresses.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
    return "That does not look like an email address."
  if (FREE_MAIL.includes(email.trim().split("@")[1].toLowerCase()))
    return "Use your work address."
  return null
}

const PricingGate = ({
  offer,
  label = "Reveal the price",
  className,
  // "button" is the prominent form — the shared blue fill, not a text link.
  // An underlined link is the right weight for a cross-reference and the wrong
  // weight for the one action a gated offer page is built around.
  variant = "button",
}) => {
  const [open, setOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [form, setForm] = useState({ name: "", company: "", email: "" })

  const { unlocked } = usePricingUnlocked()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const dialogRef = useRef(null)
  const triggerRef = useRef(null)
  const firstFieldRef = useRef(null)

  const close = useCallback(() => {
    setOpen(false)
    setError(null)
    setSending(false)
  }, [])

  // Escape closes, Tab is trapped inside.
  useEffect(() => {
    if (!open) return
    const onKeyDown = e => {
      if (e.key === "Escape") {
        e.stopPropagation()
        close()
        return
      }
      if (e.key !== "Tab") return
      const nodes = dialogRef.current
        ? [...dialogRef.current.querySelectorAll(FOCUSABLE)]
        : []
      if (!nodes.length) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open, close])

  // Lock background scroll, move focus in, hand it back on close.
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const target = firstFieldRef.current || dialogRef.current
    if (target) target.focus()
    return () => {
      document.body.style.overflow = prevOverflow
      const back = triggerRef.current || previous
      if (back && typeof back.focus === "function") back.focus()
    }
  }, [open])

  const onSubmit = async e => {
    e.preventDefault()
    const problem = validate(form)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    setSending(true)

    let recaptchaToken = null
    try {
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("pricing_request")
      }
    } catch (err) {
      recaptchaToken = null
    }

    await submitPricingRequest({
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      offerSlug: offer.slug,
      offerName: offer.name,
      sourcePath: typeof window !== "undefined" ? window.location.pathname : "",
      recaptchaToken,
    })

    // No confirmation screen. The price appearing where the button was IS the
    // confirmation, and it is the thing they came for.
    setSending(false)
    setOpen(false)
  }

  const field = key => ({
    value: form[key],
    onChange: e => setForm(f => ({ ...f, [key]: e.target.value })),
  })

  // Once unlocked the ask is gone; the call site renders the price instead.
  if (unlocked) return null

  const titleId = `pg-title-${offer.slug}`

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={[
          "pg-trigger",
          variant === "button" ? "pg-trigger--button" : "",
          className || "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => setOpen(true)}
      >
        {label} <span aria-hidden="true">&rarr;</span>
        <span className="pg-trigger__sr"> for {offer.name}</span>
      </button>

      {open && (
        <div
          className="pg-overlay"
          onMouseDown={e => e.target === e.currentTarget && close()}
        >
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <div className="pg-modal__head">
              <span className="pg-modal__tag">{offer.name}</span>
              <button
                type="button"
                className="pg-modal__close"
                onClick={close}
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>

            <form className="pg-form" onSubmit={onSubmit} noValidate>
              <h2 className="pg-form__heading" id={titleId}>
                The full scope and pricing
              </h2>
              {/* Two things fixed here. It used to open with the price itself —
                  "$6,000 setup + $250/month is the starting figure" — giving
                  away the one thing the form exists to reveal. And it promised
                  "the payment schedule, the tiers and what is not included",
                  which only four of the fifteen gated offers actually carry;
                  the other eleven have no priceDetail and no exclusions written
                  yet, so that sentence would have over-promised on most of the
                  pages it appears on. */}
              <p className="pg-form__lede">
                Tell us who you are and the price appears on this page, with
                everything else this offer has scoped. Nothing is emailed, and
                you will not be asked again.
              </p>

              <label className="pg-field">
                <span className="pg-field__label">Name</span>
                <input
                  className="pg-field__input"
                  type="text"
                  autoComplete="name"
                  ref={firstFieldRef}
                  {...field("name")}
                />
              </label>

              <label className="pg-field">
                <span className="pg-field__label">Company</span>
                <input
                  className="pg-field__input"
                  type="text"
                  autoComplete="organization"
                  {...field("company")}
                />
              </label>

              <label className="pg-field">
                <span className="pg-field__label">Work email</span>
                <input
                  className="pg-field__input"
                  type="email"
                  autoComplete="email"
                  {...field("email")}
                />
              </label>

              {error && (
                <p className="pg-form__error" role="alert">
                  {error}
                </p>
              )}

              <button
                className="pg-btn pg-btn--primary"
                type="submit"
                disabled={sending}
              >
                {sending ? "One moment…" : "Show me the price"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * The line that sits beside a revealed price. Named rather than inlined so the
 * wording lives in one place across every call site.
 */
export const PricingRecognised = ({ className }) => {
  const { unlocked, name } = usePricingUnlocked()
  if (!unlocked) return null
  return (
    <p className={className ? `pg-recognised ${className}` : "pg-recognised"}>
      {name ? `Good to see you again, ${name} — ` : "Recognised — "}
      prices are shown for you.
    </p>
  )
}

export default PricingGate
